// components/ui/ResizablePanel.tsx
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';

const HANDLE_WIDTH_PX = 12;
const SNAP_OPEN_THRESHOLD_PX = 20; // Min drag distance from collapsed state to snap open
const TOGGLE_ANIMATION_MS = 120;
// Bezier fit of the Studio wheel-zoom step as the eye perceives it (see WHEEL_ZOOM_PERCEIVED_EASING in kernel/useZoom.ts).
const TOGGLE_ANIMATION_EASING = 'cubic-bezier(0.333, 0.396, 0.667, 0.717)';

export interface ResizablePanelRef {
  toggleCollapse: (forceOpenToWidth?: number, userGesture?: boolean) => void;
  isCurrentlyCollapsed: () => boolean;
  getCurrentContentWidth: () => number;
}

interface ResizablePanelProps {
  children: React.ReactNode;
  side: 'left' | 'right';
  initialWidth: number | string;
  minWidthPx: number;
  maxWidthPx?: number;
  className?: string;
  style?: React.CSSProperties;
  handleClassName?: string;
  onWidthChange?: (newContentWidth: number, isEffectivelyCollapsed: boolean) => void;
  collapsedWidthPx?: number;
  collapseThresholdFactor?: number;
  id?: string;
  isVisible?: boolean;
  /**
   * When true, a collapsed panel refuses the expand gestures (double-click and drag on the
   * handle). For a panel whose content decided there is nothing to show (the Source dashboard),
   * expanding by gesture opens an empty strip the auto-close effect will not re-close, because
   * that effect only runs when the content answer CHANGES.
   */
  disableExpandGesture?: boolean;
  /** Mounts collapsed while keeping initialWidth as the width it reopens to. */
  startCollapsed?: boolean;
  /** Fires only for a user's own collapse or expand (drag, double-click, toggleCollapse with userGesture), never for code. */
  onUserCollapseChange?: (collapsed: boolean) => void;
}

const ResizablePanel = forwardRef<ResizablePanelRef, ResizablePanelProps>(({
  children,
  side,
  initialWidth,
  minWidthPx,
  maxWidthPx,
  className = '',
  style = {},
  handleClassName = '',
  onWidthChange,
  collapsedWidthPx = 0,
  collapseThresholdFactor = 0.5,
  id,
  isVisible = true,
  disableExpandGesture = false,
  startCollapsed = false,
  onUserCollapseChange,
}, ref) => {
  const [contentWidth, setContentWidth] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isPanelEffectivelyCollapsed, setIsPanelEffectivelyCollapsed] = useState<boolean>(false);
  const [lastNonCollapsedWidth, setLastNonCollapsedWidth] = useState<number>(minWidthPx);
  const [isHandleHovered, setIsHandleHovered] = useState<boolean>(false);
  const [isToggleAnimating, setIsToggleAnimating] = useState<boolean>(false);
  const toggleAnimationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ x: number; initialContentWidth: number; wasCollapsed: boolean; collapsed: boolean } | null>(null);
  const resolvedInitialWidthPxRef = useRef<number>(0);
  const isInitializedRef = useRef(false);

  // Only open/collapse flips animate; a drag must keep following the cursor with no lag. The timer is never cleared
  // on unmount: StrictMode's simulated unmount would leave the flag on, and a collapsed panel's handle clipped.
  const animateNextToggle = useCallback(() => {
    if (toggleAnimationTimerRef.current) clearTimeout(toggleAnimationTimerRef.current);
    setIsToggleAnimating(true);
    toggleAnimationTimerRef.current = setTimeout(() => setIsToggleAnimating(false), TOGGLE_ANIMATION_MS);
  }, []);

  const collapseAtPx = useMemo(() => minWidthPx * collapseThresholdFactor, [minWidthPx, collapseThresholdFactor]);

  // Sync content width when collapsedWidthPx changes while collapsed (e.g. mobile/desktop switch)
  useEffect(() => {
    if (isPanelEffectivelyCollapsed) {
      setContentWidth(collapsedWidthPx);
    }
  }, [collapsedWidthPx, isPanelEffectivelyCollapsed]);

  useEffect(() => {
    if (isInitializedRef.current) return;

    let newInitialContentWidthPx: number;
    const parentElement = panelRef.current?.parentElement;

    if (typeof initialWidth === 'string' && initialWidth.endsWith('%')) {
      const percentage = parseFloat(initialWidth);
      const parentWidth = parentElement?.getBoundingClientRect().width;
      if (parentWidth && parentWidth > 0) {
        newInitialContentWidthPx = (parentWidth * percentage) / 100;
      } else {
        newInitialContentWidthPx = typeof initialWidth === 'number' ? initialWidth : minWidthPx;
      }
    } else {
      newInitialContentWidthPx = Number(initialWidth);
    }
    
    resolvedInitialWidthPxRef.current = newInitialContentWidthPx;

    let effectiveContentWidth = newInitialContentWidthPx;
    let initiallyCollapsed = false;

    if (newInitialContentWidthPx < collapseAtPx && newInitialContentWidthPx >= collapsedWidthPx) {
        effectiveContentWidth = collapsedWidthPx;
        initiallyCollapsed = true;
    } else if (newInitialContentWidthPx < collapsedWidthPx) {
        effectiveContentWidth = collapsedWidthPx;
        initiallyCollapsed = true;
    } else {
        effectiveContentWidth = Math.max(minWidthPx, Math.min(newInitialContentWidthPx, maxWidthPx ?? Infinity));
        initiallyCollapsed = startCollapsed;
        setLastNonCollapsedWidth(effectiveContentWidth);
        if (startCollapsed) effectiveContentWidth = collapsedWidthPx;
    }
    
    setContentWidth(effectiveContentWidth);
    setIsPanelEffectivelyCollapsed(initiallyCollapsed);
    isInitializedRef.current = true;

  }, [initialWidth, minWidthPx, maxWidthPx, collapsedWidthPx, collapseAtPx, startCollapsed]);


  const handleHandleMouseEnter = useCallback(() => setIsHandleHovered(true), []);
  const handleHandleMouseLeave = useCallback(() => setIsHandleHovered(false), []);

  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    if (disableExpandGesture && isPanelEffectivelyCollapsed) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
    dragStartRef.current = {
      x: event.clientX,
      initialContentWidth: contentWidth, 
      wasCollapsed: isPanelEffectivelyCollapsed,
      collapsed: isPanelEffectivelyCollapsed,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [contentWidth, isPanelEffectivelyCollapsed, disableExpandGesture]);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging || !dragStartRef.current) return;
    event.preventDefault();
    event.stopPropagation();

    const deltaX = event.clientX - dragStartRef.current.x;
    let newDesiredContentWidth: number;

    if (side === 'right') {
      newDesiredContentWidth = dragStartRef.current.initialContentWidth - deltaX;
    } else { 
      newDesiredContentWidth = dragStartRef.current.initialContentWidth + deltaX;
    }

    let newContentWidthPx = newDesiredContentWidth;
    let nextCollapsedState = isPanelEffectivelyCollapsed;

    if (dragStartRef.current.wasCollapsed) {
        const openThreshold = (dragStartRef.current.initialContentWidth === 0 && collapsedWidthPx === 0) 
                              ? SNAP_OPEN_THRESHOLD_PX 
                              : collapsedWidthPx + SNAP_OPEN_THRESHOLD_PX;

        if (newDesiredContentWidth > openThreshold) {
            newContentWidthPx = Math.max(minWidthPx, Math.min(newDesiredContentWidth, maxWidthPx ?? Infinity));
            setLastNonCollapsedWidth(newContentWidthPx);
            nextCollapsedState = false;
        } else {
            newContentWidthPx = collapsedWidthPx; 
            nextCollapsedState = true;
        }
    } else { 
      if (newDesiredContentWidth < collapseAtPx) {
        if (contentWidth > collapsedWidthPx && collapsedWidthPx === 0) { 
            setLastNonCollapsedWidth(contentWidth);
        }
        newContentWidthPx = collapsedWidthPx;
        nextCollapsedState = true;
      } else {
        newContentWidthPx = Math.max(minWidthPx, Math.min(newDesiredContentWidth, maxWidthPx ?? Infinity));
        setLastNonCollapsedWidth(newContentWidthPx);
        nextCollapsedState = false;
      }
    }
    
    if (newContentWidthPx < 0 && collapsedWidthPx === 0) {
        newContentWidthPx = 0;
    } else if (newContentWidthPx < collapsedWidthPx) {
        newContentWidthPx = collapsedWidthPx; 
    }

    setContentWidth(newContentWidthPx);
    dragStartRef.current.collapsed = nextCollapsedState;
    if (isPanelEffectivelyCollapsed !== nextCollapsedState) {
      animateNextToggle();
      setIsPanelEffectivelyCollapsed(nextCollapsedState);
    }
    if (onWidthChange) {
      onWidthChange(newContentWidthPx, nextCollapsedState);
    }
  }, [isDragging, side, minWidthPx, maxWidthPx, onWidthChange, collapsedWidthPx, collapseAtPx, isPanelEffectivelyCollapsed, contentWidth, animateNextToggle]);

  const handleMouseUp = useCallback((event: MouseEvent) => {
    if (!isDragging) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (dragStartRef.current && dragStartRef.current.wasCollapsed !== dragStartRef.current.collapsed) {
      onUserCollapseChange?.(dragStartRef.current.collapsed);
    }
    dragStartRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isDragging, onUserCollapseChange]);

    const handleDoubleClickOnHandle = useCallback(() => {
    if (disableExpandGesture && isPanelEffectivelyCollapsed) return;
    let newContentWidthPx: number;
    let nextCollapsedState: boolean;

    if (isPanelEffectivelyCollapsed) {
      let targetOpenWidth = lastNonCollapsedWidth;
      if (targetOpenWidth < minWidthPx || targetOpenWidth <= collapsedWidthPx) {
          targetOpenWidth = Math.max(minWidthPx, Math.min(resolvedInitialWidthPxRef.current, maxWidthPx ?? Infinity));
      }
      if (targetOpenWidth < minWidthPx || targetOpenWidth <= collapsedWidthPx) {
          targetOpenWidth = minWidthPx;
      }
      
      newContentWidthPx  = targetOpenWidth;
      nextCollapsedState = false;
      setLastNonCollapsedWidth(newContentWidthPx);
    } else {
      if (contentWidth > collapsedWidthPx && collapsedWidthPx === 0) {
        setLastNonCollapsedWidth(contentWidth);
      }
      
      newContentWidthPx  = collapsedWidthPx;
      nextCollapsedState = true;
    }
    
    animateNextToggle();
    setContentWidth(newContentWidthPx);
    if (isPanelEffectivelyCollapsed !== nextCollapsedState) {
      setIsPanelEffectivelyCollapsed(nextCollapsedState);
    }
    if (onWidthChange) {
      onWidthChange(newContentWidthPx, nextCollapsedState);
    }
    onUserCollapseChange?.(nextCollapsedState);
  }, [ isPanelEffectivelyCollapsed, lastNonCollapsedWidth, minWidthPx, maxWidthPx, collapsedWidthPx, resolvedInitialWidthPxRef, contentWidth, onWidthChange, disableExpandGesture, animateNextToggle, onUserCollapseChange ]);


  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  useImperativeHandle(ref, () => ({
    toggleCollapse: (forceOpenToWidth?: number, userGesture?: boolean) => {
      const targetOpenWidth = forceOpenToWidth || lastNonCollapsedWidth || minWidthPx;
      // Compute the next state, then apply state + side effects OUTSIDE any setState updater, so we
      // never call a parent setState (onWidthChange) while React is mid-render of this component.
      const willBeCollapsed = !isPanelEffectivelyCollapsed;
      let newContentWidth: number;
      if (willBeCollapsed) {
        if (contentWidth > collapsedWidthPx && collapsedWidthPx === 0) {
          setLastNonCollapsedWidth(contentWidth);
        }
        newContentWidth = collapsedWidthPx;
      } else {
        newContentWidth = targetOpenWidth;
        setLastNonCollapsedWidth(targetOpenWidth);
      }
      animateNextToggle();
      setContentWidth(newContentWidth);
      setIsPanelEffectivelyCollapsed(willBeCollapsed);
      if (onWidthChange) {
        onWidthChange(newContentWidth, willBeCollapsed);
      }
      if (userGesture) onUserCollapseChange?.(willBeCollapsed);
    },
    isCurrentlyCollapsed: () => isPanelEffectivelyCollapsed,
    getCurrentContentWidth: () => contentWidth,
  }), [isPanelEffectivelyCollapsed, contentWidth, collapsedWidthPx, lastNonCollapsedWidth, minWidthPx, onWidthChange, onUserCollapseChange, animateNextToggle]);

  const panelWrapperTotalWidth = (isPanelEffectivelyCollapsed)
    ? collapsedWidthPx
    : contentWidth + HANDLE_WIDTH_PX; 

  // While collapsing, the content keeps its open width and is clipped by the shrinking wrapper instead of reflowing every frame.
  const renderedContentWidth = isPanelEffectivelyCollapsed && isToggleAnimating ? lastNonCollapsedWidth : contentWidth;
  const contentHostStyle: React.CSSProperties = {
    width: `${renderedContentWidth}px`,
    minWidth: `${renderedContentWidth}px`,
    height: '100%',
    overflow: 'hidden', 
    display: 'flex',
    flexDirection: 'column',
    visibility: renderedContentWidth === 0 ? 'hidden' : 'visible',
  };
  
  if (renderedContentWidth === 0) {
    contentHostStyle.padding = '0';
    contentHostStyle.margin = '0';
    contentHostStyle.borderWidth = '0';
  }

  let handleBaseBgColor = 'rgba(var(--FATER-PRIMARY-RGB),0.1)';
  if (isDragging) {
    handleBaseBgColor = 'rgba(var(--FATER-PRIMARY-RGB),0.3)';
  } else if (isPanelEffectivelyCollapsed && collapsedWidthPx === 0) {
    handleBaseBgColor = 'transparent'; // Super-collapsed, handle is transparent
  }

  const showCollapsedHoverCue = isPanelEffectivelyCollapsed && collapsedWidthPx === 0 && isHandleHovered;
  const isCollapsedLeftGripOverlay =
    isPanelEffectivelyCollapsed && side === 'left' && collapsedWidthPx > 0;

  const handleElement = (
    <div
      className={`${handleClassName} resizable-panel-handle ${isDragging ? 'dragging' : ''}`}
      onMouseEnter={handleHandleMouseEnter}
      onMouseLeave={handleHandleMouseLeave}
      style={{
        width: `${isCollapsedLeftGripOverlay ? collapsedWidthPx : HANDLE_WIDTH_PX}px`,
        minWidth: `${isCollapsedLeftGripOverlay ? collapsedWidthPx : HANDLE_WIDTH_PX}px`,
        height: '100%',
        cursor: 'col-resize',
        backgroundColor: handleBaseBgColor,
        flexShrink: 0,
        display: 'flex',
        position: 'relative', // For positioning the hover cue
        zIndex: isDragging ? 120 : 40, // Keep the handle above adjacent overlapping panes
        ...(isCollapsedLeftGripOverlay && {
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: isDragging ? 120 : 40,
        }),
        ...( (isPanelEffectivelyCollapsed && collapsedWidthPx === 0) && {
          position: 'absolute',
          top: 0,
          ...(side === 'right' ? { left: `-${HANDLE_WIDTH_PX}px` } : { left: '0px' }),
          zIndex: isDragging ? 120 : 40,
        })
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClickOnHandle}
      title="Drag to resize"
    >
      {/* Hover Cue Element */}
      {showCollapsedHoverCue && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: '1px', // Thin line
            backgroundColor: 'rgba(179, 217, 255, 0.5)',
            ...(side === 'right'
              ? { right: 0 } // For right panel, cue is on the right edge of the (transparent) handle
              : { left: 0 }),  // For left panel, cue is on the left edge of the (transparent) handle
          }}
        />
      )}
    </div>
  );

  const contentElement = (
    <div
      className="resizable-panel-content-host"
      style={contentHostStyle}
    >
      {children}
    </div>
  );

  return (
    <div
      ref={panelRef}
      id={id}
      className={`${className} resizable-panel-wrapper`}
      style={{
        ...style, 
        display: isVisible ? 'flex' : 'none',
        width: `${panelWrapperTotalWidth}px`,
        minWidth: `${
          (isPanelEffectivelyCollapsed)
            ? collapsedWidthPx
            : (Math.max(collapsedWidthPx, minWidthPx) + HANDLE_WIDTH_PX)
        }px`,
        flexShrink: 0, 
        position: 'relative',
        overflow: (isPanelEffectivelyCollapsed && collapsedWidthPx === 0 && !isToggleAnimating) ? 'visible' : 'hidden',
        transition: isToggleAnimating
          ? `width ${TOGGLE_ANIMATION_MS}ms ${TOGGLE_ANIMATION_EASING}, min-width ${TOGGLE_ANIMATION_MS}ms ${TOGGLE_ANIMATION_EASING}`
          : 'none',
      }}
    >
      {side === 'right' ? (
        <>
          {handleElement}
          {contentElement}
        </>
      ) : ( 
        <>
          {contentElement}
          {handleElement}
        </>
      )}
    </div>
  );
});

ResizablePanel.displayName = 'ResizablePanel';
export default React.memo(ResizablePanel);
